import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import ShowTime from '../../utils/ShowTime/ShowTime';
import CustomLoader from '../../utils/DataTableLoader/CustomLoader';
import './ActivityLogModal.css';

const ActivityLogModal = ({
  show,
  onHide,
  sourceType = "TrialLicenses",
  sourceId,
  title = "Activity Logs",
  activityTypeOptions = [
    { value: "assigned", label: "Assigned", color: "#1976d2" },
    { value: "verified", label: "Verified", color: "#2e7d32" },
    { value: "verfied_locked", label: "Verified & Locked", color: "#0288d1" },
    { value: "assigned_locked", label: "Assigned & Locked", color: "#0288d1" },
    { value: "unassigned", label: "Unassigned", color: "#0288d1" },
    { value: "unlocked", label: "Unlocked", color: "#0288d1" },
    { value: "verfied_locked-unassigned", label: "Verified & Unassigned", color: "#2e7d32" },
    { value: "verfied_locked-unlocked", label: "Verified & Unlocked", color: "#2e7d32" },
    { value: "assigned_locked-unassigned", label: "Assigned & Unassigned", color: "#2e7d32" },
    { value: "assigned_locked-unlocked", label: "Assigned & Unlocked", color: "#2e7d32" },
  ]
}) => {
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("start_time");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);

  const getActivityTypeConfig = (activityType) => {
    return activityTypeOptions.find(option => option.value === activityType) ||
      { value: activityType, label: activityType, color: "#6c757d" };
  };

  const getTimeDifference = (currentTime, nextTime) => {
    if (!nextTime || !currentTime) return null;

    const current = new Date(currentTime);
    const next = new Date(nextTime);
    const diffMs = current - next;

    // If timestamps are equal or reversed (future before past), skip
    if (diffMs <= 0) return null;

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return diffDays === 1 ? '1 day later' : `${diffDays} days later`;
    } else if (diffHours > 0) {
      return diffHours === 1 ? '1 hour later' : `${diffHours} hours later`;
    } else if (diffMinutes > 0) {
      return diffMinutes === 1 ? '1 minute later' : `${diffMinutes} minutes later`;
    } else {
      return 'Right after';
    }
  };
  const getFormattedDuration = (start, end) => {
    if (!start || !end) return null;
    const diffMs = new Date(end) - new Date(start);
    if (diffMs <= 0) return null;

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);

    if (diffHours > 0) {
      const mins = diffMinutes % 60;
      return `${diffHours}h ${mins}m`;
    } else if (diffMinutes > 0) {
      const secs = diffSeconds % 60;
      return `${diffMinutes}m ${secs}s`;
    } else {
      return `${diffSeconds}s`;
    }
  };

  const columns = [
    {
      name: 'Type',
      selector: row => row.activity_type,
      sortable: true,
      sortField: "activity_type",
      cell: row => {
        const config = getActivityTypeConfig(row.activity_type);
        return (
          <div className="activity-type-cell">
            <span
              className="activity-badge"
              style={{
                backgroundColor: `${config.color}20`,
                color: config.color,
                borderColor: `${config.color}40`
              }}
            >
              {config.label}
            </span>
          </div>
        );
      },
    },
    {
      name: 'Performed at',
      selector: row => row.start_time,
      sortable: true,
      sortField: "start_time",
      minWidth: '200px',
      cell: (row, index) => {
        const nextTime = tableData[index + 1]?.start_time || null;
        let timeDiff = null;

        // Show difference if next row exists
        if (nextTime) {
          timeDiff = getTimeDifference(row.start_time, nextTime, index);
        }

        const duration = getFormattedDuration(row.start_time, row.end_time);

        return (
          <div style={{ whiteSpace: 'pre-wrap', padding: '10px 0' }}>
            <div style={{ fontWeight: '500' }}>{row.start_time ? ShowTime(row.start_time) : '-'}</div>
            {row.end_time && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                to {ShowTime(row.end_time)}
                {duration && <span style={{ color: '#2e7d32', fontWeight: 'bold' }}> ({duration})</span>}
              </div>
            )}
            {timeDiff && (
              <div
                style={{
                  fontSize: '11px',
                  color: '#6c757d',
                  fontStyle: 'italic',
                  marginTop: '4px',
                  padding: '2px 8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  display: 'inline-block'
                }}
              >
                {timeDiff}
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: 'Machine Info',
      selector: row => row.machine_id,
      minWidth: '250px',
      cell: row => (
        <div style={{ fontSize: '12px', padding: '10px 0' }}>
          {row.machine_id && (
            <div>
              <span style={{ color: '#666' }}>ID: </span>
              <span style={{ fontFamily: 'monospace', backgroundColor: '#f0f0f0', padding: '2px 4px', borderRadius: '3px' }}>{row.machine_id}</span>
            </div>
          )}
          {row.machine_user && (
            <div style={{ marginTop: '4px' }}>
              <span style={{ color: '#666' }}>User: </span>
              <span>{row.machine_user}</span>
            </div>
          )}
          {row.product_version && (
            <div style={{ marginTop: '4px' }}>
              <span style={{ color: '#666' }}>Version: </span>
              <span className="badge bg-light text-dark border">{row.product_version}</span>
            </div>
          )}
          {!row.machine_id && !row.machine_user && !row.product_version && '-'}
        </div>
      )
    }


  ];


  const getActivityLogs = async () => {
    if (!sourceId) return;

    try {
      setPending(true);
      setLoading(true);

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/activity_logs`, {
        params: {
          limit: pageSize,
          page: page,
          sort_by: sortBy,
          sort_order: sortOrder,
          'filters[0][column]': 'source_type',
          'filters[0][operator]': '=',
          'filters[0][value]': sourceType,
          'filters[1][column]': 'source_id',
          'filters[1][operator]': '=',
          'filters[1][value]': sourceId,
        },
        headers: {
          'access-token': localStorage.getItem('accessToken'),
        },
      });

      setLoading(false);
      setPending(false);

      if (response.data.status) {
        setTotalRecords(response.data.meta.pagination.total);
        setTableData(response.data.data);
      } else {
        toast.error('Failed to fetch activity logs');
        console.log(response.data);
      }
    } catch (error) {
      setLoading(false);
      setPending(false);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error('Failed to fetch activity logs');
      }
    }
  };

  useEffect(() => {
    if (show && sourceId) {
      getActivityLogs();
    }
  }, [show, sourceId, page, pageSize, sortBy, sortOrder]);

  const handleClose = () => {
    setTableData([]);
    setTotalRecords(0);
    setPage(1);
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="activity-log-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="table-wrapper">
          <DataTable
            columns={columns}
            data={tableData}
            dense
            direction="auto"
            highlightOnHover
            noHeader
            persistTableHead
            pointerOnHover
            responsive
            subHeaderAlign="right"
            subHeaderWrap
            progressPending={pending}
            progressComponent={<CustomLoader />}
            pagination
            paginationServer
            paginationPerPage={pageSize}
            paginationTotalRows={totalRecords}
            paginationRowsPerPageOptions={[10, 20, 50, 100, 150, 500, 1000, 1500]}
            onChangePage={(page) => setPage(page)}
            onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
              setPageSize(currentRowsPerPage);
              setPage(currentPage);
            }}
            sortServer
            onSort={(column, sortDirection) => {
              setSortBy(column.sortField);
              setSortOrder(sortDirection);
            }}
            conditionalRowStyles={[
              {
                when: row => row.activity_type === 'trial',
                style: {
                  backgroundColor: 'rgba(10, 0, 72, 0.1)',
                },
              },
            ]}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ActivityLogModal;
